import React, { useEffect, useState, useCallback } from "react";
import {
  Wallet, ArrowUpCircle, ArrowDownCircle, RefreshCw, Loader2,
  Calendar, ChevronUp, ChevronDown, Download, ArrowRightLeft,
} from "lucide-react";
import { api } from "@/lib/api";

const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TYPE_LABELS = { credit: "Credit", debit: "Debit", refund: "Refund" };
const REF_LABELS = { top_up: "Top-up", recharge: "Recharge", refund: "Refund", manual_adjustment: "Adjustment" };

const typeBadge = (type) => {
  const cls = type === "credit" || type === "refund"
    ? "bg-green-50 text-green-700 border-green-200"
    : "bg-red-50 text-red-700 border-red-200";
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>
      {type === "credit" && <ArrowDownCircle className="w-3 h-3" />}
      {type === "debit" && <ArrowUpCircle className="w-3 h-3" />}
      {type === "refund" && <RefreshCw className="w-3 h-3" />}
      {TYPE_LABELS[type] || type}
    </span>
  );
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const TABS = [
  { key: "all", label: "All Transactions" },
  { key: "credits", label: "Balance Received" },
];

export default function DealerWallet() {
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState({ items: [], total: 0, page: 1, limit: 25 });
  const [credits, setCredits] = useState({ items: [], total: 0, page: 1, limit: 25, grand_total: 0 });
  const [loading, setLoading] = useState(true);
  const [tblLoading, setTblLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  // Filters — shared
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const LIMIT = 25;

  const loadWallet = async () => {
    try {
      const { data } = await api.get("/dealer/wallet");
      setWallet(data);
    } catch {}
  };

  const loadTxns = useCallback(async () => {
    setTblLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", page);
      params.set("limit", LIMIT);
      params.set("sort_order", sortOrder);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const { data } = await api.get(`/dealer/wallet/transactions?${params.toString()}`);
      setTxns(data);
    } catch {}
    setTblLoading(false);
  }, [page, sortOrder, dateFrom, dateTo]);

  const loadCredits = useCallback(async () => {
    setTblLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", page);
      params.set("limit", LIMIT);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const { data } = await api.get(`/dealer/wallet/credits?${params.toString()}`);
      setCredits(data);
    } catch {}
    setTblLoading(false);
  }, [page, dateFrom, dateTo]);

  useEffect(() => {
    setLoading(true);
    loadWallet().finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activeTab === "all") loadTxns();
    else loadCredits();
  }, [activeTab, loadTxns, loadCredits]);

  const toggleSort = () => {
    setSortOrder((s) => (s === "desc" ? "asc" : "desc"));
    setPage(1);
  };

  const clearDates = () => { setDateFrom(""); setDateTo(""); setPage(1); };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setDateFrom("");
    setDateTo("");
  };

  const currentData = activeTab === "all" ? txns : credits;
  const pageCount = Math.max(1, Math.ceil(currentData.total / LIMIT));

  // CSV export
  const exportCSV = () => {
    if (activeTab === "all") {
      const headers = ["#", "Date", "Type", "Reference", "Opening", "Credit", "Debit", "Closing", "Note"];
      const csvRows = [headers.join(",")];
      txns.items.forEach((t, i) => {
        csvRows.push([
          t.seq_no || i + 1, `"${fmtDate(t.date)}"`,
          TYPE_LABELS[t.type] || t.type,
          REF_LABELS[t.reference_type] || t.reference_type || "—",
          t.opening_balance.toFixed(2), t.credit.toFixed(2),
          t.debit.toFixed(2), t.closing_balance.toFixed(2),
          `"${(t.note || "").replace(/"/g, '""')}"`,
        ].join(","));
      });
      downloadCSV(csvRows, "wallet_transactions");
    } else {
      const headers = ["#", "Date", "Type", "Amount (₹)", "Balance After (₹)", "Note"];
      const csvRows = [headers.join(",")];
      credits.items.forEach((t, i) => {
        csvRows.push([
          t.seq_no || i + 1, `"${fmtDate(t.date)}"`,
          REF_LABELS[t.reference_type] || t.type,
          t.amount.toFixed(2), t.balance_after.toFixed(2),
          `"${(t.note || "").replace(/"/g, '""')}"`,
        ].join(","));
      });
      downloadCSV(csvRows, "balance_received");
    }
  };

  const downloadCSV = (rows, prefix) => {
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${prefix}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div className="text-slate-400 pt-8 text-center">
      <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
      Loading wallet...
    </div>
  );

  return (
    <div className="space-y-6">
      <h2 className="font-heading font-bold text-2xl text-brand-nav">Wallet & Transactions</h2>

      {/* Balance Card */}
      <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-3 mb-3">
          <Wallet className="w-7 h-7 opacity-80" />
          <span className="text-sm font-medium opacity-80">Current Balance</span>
        </div>
        <p className="font-heading font-bold text-4xl">
          {wallet ? fmt(wallet.balance) : "₹0.00"}
        </p>
        <div className="flex gap-6 mt-4 text-sm opacity-75">
          <span>Total Credited: {wallet ? fmt(wallet.total_credited) : "₹0"}</span>
          <span>Total Debited: {wallet ? fmt(wallet.total_debited) : "₹0"}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => switchTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-semibold transition-all ${
              activeTab === tab.key
                ? "bg-white text-brand-primary shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}>
            {tab.key === "credits" && <ArrowRightLeft className="w-4 h-4" />}
            {tab.key === "all" && <Wallet className="w-4 h-4" />}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Credits summary card — only in credits tab */}
      {activeTab === "credits" && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-green-600 uppercase tracking-wider">Total Balance Received</p>
            <p className="font-heading font-bold text-3xl text-green-700 mt-1">{fmt(credits.grand_total)}</p>
            <p className="text-xs text-green-500 mt-1">{credits.total} transfer{credits.total !== 1 ? "s" : ""} from admin</p>
          </div>
          <ArrowDownCircle className="w-12 h-12 text-green-300" />
        </div>
      )}

      {/* Table Card */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" style={{ boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}>
        {/* Header + Filters */}
        <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/80">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h3 className="font-heading font-bold text-base text-brand-nav">
              {activeTab === "all" ? "Transaction Report" : "Balance Received History"}
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                  className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary/30" />
                <span className="text-xs text-slate-400">to</span>
                <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                  className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary/30" />
              </div>
              {(dateFrom || dateTo) && (
                <button onClick={clearDates} className="text-xs text-red-500 hover:text-red-700 font-semibold">Clear</button>
              )}
              <button onClick={exportCSV} disabled={currentData.items.length === 0}
                className="inline-flex items-center gap-1 text-xs font-semibold text-brand-primary bg-brand-primary/10 hover:bg-brand-primary/20 px-3 py-1.5 rounded-lg transition disabled:opacity-40">
                <Download className="w-3 h-3" /> CSV
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {activeTab === "all" ? (
            /* ── ALL TRANSACTIONS TABLE ── */
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 w-10">#</th>
                  <th className="text-left px-4 py-3">
                    <button onClick={toggleSort} className="flex items-center gap-1 hover:text-slate-700 transition">
                      Date
                      {sortOrder === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
                    </button>
                  </th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Reference</th>
                  <th className="text-right px-4 py-3">Opening Bal.</th>
                  <th className="text-right px-4 py-3 text-green-600">Credit</th>
                  <th className="text-right px-4 py-3 text-red-600">Debit</th>
                  <th className="text-right px-4 py-3">Closing Bal.</th>
                  <th className="text-left px-4 py-3">Note</th>
                </tr>
              </thead>
              <tbody>
                {tblLoading ? (
                  <tr><td colSpan={9} className="text-center py-12 text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading...
                  </td></tr>
                ) : txns.items.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-12 text-slate-400 text-sm">
                    No transactions found.{(dateFrom || dateTo) ? " Try adjusting your date filter." : ""}
                  </td></tr>
                ) : (
                  txns.items.map((t, i) => (
                    <tr key={t.id || i} className="border-t border-slate-100 hover:bg-slate-50/50 transition">
                      <td className="px-4 py-3 text-xs text-slate-400 font-mono">{t.seq_no || "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{fmtDate(t.date)}</td>
                      <td className="px-4 py-3">{typeBadge(t.type)}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-medium">
                          {REF_LABELS[t.reference_type] || t.reference_type || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-slate-600">{fmt(t.opening_balance)}</td>
                      <td className="px-4 py-3 text-right font-mono text-xs font-semibold text-green-600">
                        {t.credit > 0 ? `+${fmt(t.credit)}` : "—"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs font-semibold text-red-600">
                        {t.debit > 0 ? `−${fmt(t.debit)}` : "—"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs font-bold text-brand-nav">{fmt(t.closing_balance)}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 max-w-[180px] truncate" title={t.note || ""}>
                        {t.note || "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            /* ── BALANCE RECEIVED TABLE ── */
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 w-10">#</th>
                  <th className="text-left px-4 py-3">Date & Time</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-right px-4 py-3 bg-green-50/50">Amount Received (₹)</th>
                  <th className="text-right px-4 py-3">Balance After (₹)</th>
                  <th className="text-left px-4 py-3">Note / Remark</th>
                </tr>
              </thead>
              <tbody>
                {tblLoading ? (
                  <tr><td colSpan={6} className="text-center py-12 text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading...
                  </td></tr>
                ) : credits.items.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12 text-slate-400 text-sm">
                    <ArrowDownCircle className="w-6 h-6 mx-auto mb-2 opacity-30" />
                    No balance transfers received yet.{(dateFrom || dateTo) ? " Try adjusting your date filter." : ""}
                  </td></tr>
                ) : (
                  credits.items.map((t, i) => (
                    <tr key={t.id || i} className="border-t border-slate-100 hover:bg-green-50/30 transition">
                      <td className="px-4 py-3 text-xs text-slate-400 font-mono">{t.seq_no || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">
                        {t.date ? (
                          <>
                            <span className="font-medium">
                              {new Date(t.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                            </span>
                            <span className="text-slate-400 ml-1">
                              {new Date(t.date).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                          t.type === "refund" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-green-50 text-green-700 border-green-200"
                        }`}>
                          {t.type === "refund" ? <RefreshCw className="w-3 h-3" /> : <ArrowDownCircle className="w-3 h-3" />}
                          {REF_LABELS[t.reference_type] || TYPE_LABELS[t.type] || t.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm bg-green-50/30">
                        <span className="font-bold text-green-600">+{fmt(t.amount)}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs font-medium text-slate-600">{fmt(t.balance_after)}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 max-w-[200px] truncate" title={t.note || ""}>
                        {t.note || "—"}
                      </td>
                    </tr>
                  ))
                )}
                {/* Page total for credits */}
                {credits.items.length > 0 && (
                  <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold text-xs">
                    <td colSpan={3} className="px-4 py-3 text-right text-slate-500 uppercase tracking-wider">Page Total</td>
                    <td className="px-4 py-3 text-right font-mono text-green-700 bg-green-50/50 text-sm">
                      +{fmt(credits.items.reduce((s, r) => s + r.amount, 0))}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {currentData.total > 0 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50/50">
            <span className="text-xs text-slate-500">
              Showing {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, currentData.total)} of {currentData.total}
            </span>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 transition">
                Previous
              </button>
              <span className="text-xs text-slate-500 font-medium">{page} / {pageCount}</span>
              <button disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 transition">
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
